/**
 * TanStack Query hooks for ProgressReport resource.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

export type ReportPeriod = 'monthly' | 'midterm' | 'final' | 'custom';
export type ReportRating = 'excellent' | 'good' | 'average' | 'needs_improvement';
export type ReportStatus = 'draft' | 'published';

export interface SessionReviewStats {
  sessionsReviewed: number;
  averages: {
    participation: number | null;
    comprehension: number | null;
    behavior: number | null;
  } | null;
  rangeStart: string | null;
  rangeEnd: string | null;
}

export interface ProgressReport {
  id: string;
  enrollmentId: string;
  branchId: string;
  branchName?: string;
  period: ReportPeriod;
  periodLabel?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  attendanceSummary?: string | null;
  academicComment?: string | null;
  behaviorComment?: string | null;
  recommendation?: string | null;
  overallRating?: ReportRating | null;
  status: ReportStatus;
  publishedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  // Joined fields
  studentName?: string;
  studentCode?: string;
  classCode?: string;
  className?: string;
  // Computed on read (single-report detail only, not in list responses)
  sessionReviewStats?: SessionReviewStats;
}

export interface ListProgressReportsParams {
  page?: number;
  pageSize?: number;
  branchId?: string;
  enrollmentId?: string;
  status?: ReportStatus;
  period?: ReportPeriod;
  // Note: server listProgressReportsSchema does not support classId or q filter params.
  // Adding them here would be silently ignored. Use enrollmentId for enrollment-scoped queries.
}

export const progressReportKeys = {
  all: ['progress-reports'] as const,
  list: (params?: ListProgressReportsParams) => ['progress-reports', 'list', params] as const,
  detail: (id: string) => ['progress-reports', 'detail', id] as const,
};

export function useProgressReports(params?: ListProgressReportsParams) {
  return useQuery({
    queryKey: progressReportKeys.list(params),
    queryFn: () =>
      api.getPage<ProgressReport>(
        '/api/progress-reports',
        params as Record<string, string | number | boolean | undefined | null>,
      ),
  });
}

export function useProgressReport(id: string) {
  return useQuery({
    queryKey: progressReportKeys.detail(id),
    queryFn: () => api.get<ProgressReport>(`/api/progress-reports/${id}`),
    enabled: !!id,
    retry: (failureCount, error) => {
      const e = error as { status?: number };
      if (e.status === 404 || e.status === 403) return false;
      return failureCount < 2;
    },
  });
}

export function useCreateProgressReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.post<ProgressReport>('/api/progress-reports', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: progressReportKeys.all });
    },
  });
}

export function useUpdateProgressReport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) =>
      api.patch<ProgressReport>(`/api/progress-reports/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: progressReportKeys.all });
      void qc.invalidateQueries({ queryKey: progressReportKeys.detail(id) });
    },
  });
}

export function useDeleteProgressReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ success: boolean }>(`/api/progress-reports/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: progressReportKeys.all });
    },
  });
}
